import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import API from '@/utils/api';
import ROLES from '@/globals/roles';
import handleRequest from '@/utils/helpers/handleRequest';
import { useRouter } from 'vue-router';
import useGlobalStore from '@/stores/globalStore';

const useAuthStore = defineStore('authStore', () => {
  const router = useRouter();
  const user = ref<TUser | null>(null);
  const company = ref<TCompany | null>(null);
  const authLoading = ref(false);
  const companyCharts = ref(null);
  const globalStore = useGlobalStore();

  const isAuth = computed(() => user.value?.id || false);
  const isAdmin = computed(() => ROLES.Admin === user.value?.role);
  const isDashboard = computed(() => {
    const role = user.value?.role;
    return ROLES.CityManager === role || ROLES.Admin === role;
  });
  const companyId = computed(() => company.value?.id || null);
  const isReported = computed(() => company.value?.reported || false);
  const isApproved = computed(() => company.value?.is_approved || false);
  const isWasPaid = computed(() => company.value?.premium_plan || false);

  const setUser = (data: TUser) => {
    user.value = data;
  };

  const setCurrentCompany = (newCompany: TCompany) => {
    company.value = newCompany;
  };

  const updateCurrentCompanyById = async (
    companyId?: number,
    force = false,
  ) => {
    if (!companyId) {
      return;
    }
    if (companyId !== company.value?.id || force) {
      const { isError, data } = await handleRequest(
        API.get(`/companies/${companyId}`),
      );
      if (!isError) {
        setCurrentCompany(data);
      }
    }
  };

  const reset = () => {
    user.value = null;
    companyCharts.value = null;
    authLoading.value = false;
  };

  const getMe = async () => {
    authLoading.value = true;
    const { isError, data } = await handleRequest(API.get('/me'));
    if (!isError) {
      setUser(data);
      await fetchUserAdvancedDate();
    }
    authLoading.value = false;
  };

  const fetchUserAdvancedDate = async () => {
    const promises: Promise<void>[] = [];
    if (isDashboard.value) {
      promises.push(globalStore.fetchDashboardAvailableYears());
    } else {
      promises.push(globalStore.fetchCompanyAvailableYears());
    }
    await Promise.all(promises);

    await updateCurrentCompanyById(globalStore.selectCompanyIdFromYear);
  };

  const onLogin = async (reqData: { email: string; password: string }) => {
    const { data, isError } = await handleRequest(async () => {
      await API.csrf();
      return API.post('/login', reqData);
    });
    if (!isError) {
      setUser(data);
      await fetchUserAdvancedDate();

      if (isDashboard.value) {
        await router.replace({ name: 'dashboard' });
        return;
      }
      if (company.value?.submitted) {
        await router.replace({ name: 'design' });
        return;
      }
      await router.replace({ name: 'company.index' });
    }
  };

  const onRegister = async (reqData: {
    first_name: string;
    last_name: string;
    company_name: string;
    postcode: string;
    city_id: string;
    province_id: string;
    responsibility: boolean;
    tos: boolean;
    full_time_staff_count: number;
    email: string;
    phone: string;
    password: string;
    password_confirmation: string;
  }) => {
    const { isError, data } = await handleRequest(async () => {
      await API.csrf();
      return API.post('/register', reqData);
    });
    if (!isError) {
      setUser(data);
      await fetchUserAdvancedDate();

      await router.replace({ name: 'company.index' });
    }
  };

  const onResetPassword = async (reqData: {
    email: string;
    token: string;
    password: string;
    password_confirmation: string;
  }) => {
    const { isError } = await handleRequest(
      API.post('/reset-password', reqData),
    );
    if (!isError) {
      await router.replace({ name: 'company.index' });
    }
  };

  const onForgotPassword = async (reqData: { email: string }) => {
    const { isError } = await handleRequest(
      API.post('/forgot-password', reqData),
    );
    if (!isError) {
      return true;
    }
  };

  const onLogout = async () => {
    const { isError } = await handleRequest(API.post('/logout'));

    if (!isError) {
      reset();
      await router.replace('/');
    }
    globalStore.reset();
  };

  const createCompanyQuestionnaireToken = async () => {
    if (companyId.value) {
      const { isError, data } = await handleRequest(
        API.post(`/companies/${companyId.value}/questionnaire-link`),
      );
      if (!isError) {
        setCurrentCompany(data);
      }
    }
  };

  const deleteCompanyQuestionnaireToken = async () => {
    if (companyId.value) {
      const { isError, data } = await handleRequest(
        API.delete(`/companies/${companyId.value}/questionnaire-link`),
      );
      if (!isError) {
        setCurrentCompany(data);
      }
    }
  };

  return {
    company,
    authLoading,
    user,
    isAuth,
    companyId,
    companyCharts,
    isDashboard,
    isReported,
    isWasPaid,
    isApproved,
    isAdmin,

    createCompanyQuestionnaireToken,
    deleteCompanyQuestionnaireToken,
    getMe,
    setUser,
    setCurrentCompany,
    updateCurrentCompanyById,
    onLogin,
    onRegister,
    onResetPassword,
    onForgotPassword,
    onLogout,
    reset,
  };
});

export default useAuthStore;
